
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import UploadConfirmationModal from './UploadConfirmationModal';
import { UPLOAD_CONFIRMATION_KEY } from '../constants';
import UploadIcon from './icons/UploadIcon';
import { useTranslation } from 'react-i18next';

interface ImageUploadViewProps {
  onImageUpload: (file: File, dataUrl: string) => void;
}

interface PendingUpload {
  file: File;
  dataUrl: string;
}

const ImageUploadView: React.FC<ImageUploadViewProps> = ({ onImageUpload }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);

  const handleConfirmUpload = () => {
    if (pendingUpload) {
      localStorage.setItem(UPLOAD_CONFIRMATION_KEY, 'true');
      onImageUpload(pendingUpload.file, pendingUpload.dataUrl);
      setPendingUpload(null);
    }
  };

  const handleCancelUpload = () => {
    setPendingUpload(null);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError(null);
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (!file.type.startsWith('image/')) {
        setError(t('imageUploadView.unsupportedFileError'));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const hasAccepted = localStorage.getItem(UPLOAD_CONFIRMATION_KEY) === 'true';
        const uploadData = { file, dataUrl: reader.result as string };
        if (hasAccepted) {
          onImageUpload(uploadData.file, uploadData.dataUrl);
        } else {
          setPendingUpload(uploadData);
        }
      };
      reader.onerror = () => {
        setError(t('imageUploadView.fileReadError'));
      }
      reader.readAsDataURL(file);
    } else {
        setError(t('imageUploadView.noFileError'));
    }
  }, [onImageUpload, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop, 
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    multiple: false,
  });

  return (
    <>
      <div className="flex flex-col items-center justify-center p-4 sm:p-6 bg-white shadow-xl rounded-lg min-h-[calc(100vh-250px)] md:min-h-[500px]">
        <div
          {...getRootProps()}
          className={`w-full max-w-lg p-6 md:p-12 border-4 border-dashed rounded-xl text-center cursor-pointer transition-all duration-300 ease-in-out ${
            isDragActive ? 'border-sky-500 bg-sky-50' : 'border-slate-300 hover:border-sky-400 hover:bg-slate-50'
          }`}
        >
          <input {...getInputProps()} />
          <div className="flex justify-center items-center mb-6">
            <UploadIcon className="w-16 h-16 md:w-20 md:h-20 text-sky-500" />
          </div>
          {isDragActive ? (
            <p className="text-xl font-semibold text-sky-600">{t('imageUploadView.dropImageHere')}</p>
          ) : (
            <>
              <p className="text-lg md:text-xl font-semibold text-slate-700 mb-2">
                {t('imageUploadView.dragOrClick')}
              </p>
              <p className="text-slate-500 text-sm md:text-base">
                {t('imageUploadView.mobileCameraHint')}
              </p>
            </>
          )}
        </div>
        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
        <p className="mt-8 text-sm text-slate-600 max-w-md text-center">
          {t('imageUploadView.supportedFormatsHint')}
        </p>
      </div>
      <UploadConfirmationModal
        isOpen={!!pendingUpload}
        onConfirm={handleConfirmUpload}
        onCancel={handleCancelUpload}
      />
    </>
  );
};

export default ImageUploadView;
    